import { BarChart3, Briefcase, MessageCircle, UserRound } from 'lucide-react';
import { TABS as TAB_CATALOGUE, type TabType } from '../../shared/tabs.ts';
import { cn } from '../lib/cn.ts';

/** Chat, then the tabs fuse opened for this user (in the order it opened them), then Profile. */
export type TabId = 'chat' | 'profile' | TabType;

const ICONS: Record<TabId, typeof MessageCircle> = {
  chat: MessageCircle,
  profile: UserRound,
  progress: BarChart3,
  interview: Briefcase,
};

export function TabBar({
  active,
  onSelect,
  generated = [],
  adminOn = false,
}: {
  active: TabId;
  onSelect: (t: TabId) => void;
  generated?: TabType[];
  /** A dot on Profile, where the admin tools are, so admin mode is never on unnoticed. */
  adminOn?: boolean;
}) {
  const TABS: Array<{ id: TabId; label: string; icon: typeof MessageCircle }> = [
    { id: 'chat', label: 'Chat', icon: ICONS.chat },
    ...generated.map((t) => ({ id: t, label: TAB_CATALOGUE[t].title, icon: ICONS[t] })),
    { id: 'profile', label: 'Profile', icon: ICONS.profile },
  ];
  return (
    <nav
      data-testid="tab-bar"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-fg/10 bg-bg pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="mx-auto flex max-w-2xl">
        {TABS.map(({ id, label, icon: Icon }) => (
          <li key={id} className="flex-1">
            <button
              type="button"
              data-testid={`tab-${id}`}
              aria-current={active === id ? 'page' : undefined}
              onClick={() => onSelect(id)}
              className={cn(
                'flex w-full flex-col items-center gap-1 py-2.5 text-xs transition',
                active === id ? 'text-fg' : 'text-subtle hover:text-fg',
              )}
            >
              <span className="relative">
                <Icon className="size-5" aria-hidden />
                {id === 'profile' && adminOn ? (
                  <span
                    data-testid="admin-dot"
                    className="absolute -right-1 -top-0.5 size-2 rounded-full bg-warn ring-2 ring-bg"
                  />
                ) : null}
              </span>
              {label}
              {id === 'profile' && adminOn ? <span className="sr-only"> (admin mode on)</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
